/**
 * AppICEMAP — Theme controller.
 * Auto-detects day/night by time of day (06:00–18:00 = day, 18:00–06:00 = night).
 * Supports localStorage override and system preference fallback.
 */

const STORAGE_KEY = "appicemap-theme";
const DAY_START = 6; // 06:00
const DAY_END = 18; // 18:00

const listeners = [];

/**
 * Get the auto-detected theme based on time of day.
 * @returns {'day' | 'night'}
 */
function getAutoTheme() {
	const hour = new Date().getHours();
	return hour >= DAY_START && hour < DAY_END ? "day" : "night";
}

/**
 * Get the current effective theme, respecting overrides.
 *
 * Reads the explicit preference first (set via the toggle in Profile); with
 * none stored, falls back to time-of-day auto-detection. This used to be
 * hardcoded to always return "night" — the day palette (colors-day.css) and
 * every consumer (MapViewport's tile switch, the meta theme-color, etc.) were
 * fully wired up, but nothing could ever reach "day": the toggle changed the
 * DOM directly for the current session, then a reload silently forced it back
 * to night, making the preference look like it was never saved.
 *
 * @returns {'day' | 'night'}
 */
export function getCurrentTheme() {
	const stored = localStorage.getItem(STORAGE_KEY);
	if (stored === "day" || stored === "night") return stored;
	return getAutoTheme();
}

/**
 * Set the theme explicitly.
 * @param {'day' | 'night' | 'auto'} theme
 */
export function setTheme(theme) {
	if (theme === "auto") {
		localStorage.removeItem(STORAGE_KEY);
	} else {
		localStorage.setItem(STORAGE_KEY, theme);
	}
	applyTheme(getCurrentTheme());
}

/**
 * Initialize theme on app startup.
 */
export function initTheme() {
	const effective = getCurrentTheme();
	applyTheme(effective);

	// Check every minute if the auto-theme should switch
	setInterval(() => {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) {
			const auto = getAutoTheme();
			const current = document.documentElement.dataset.theme;
			if (auto !== current) {
				applyTheme(auto);
			}
		}
	}, 60_000);
}

/**
 * Register a callback for theme changes.
 * @param {(theme: 'day' | 'night') => void} cb
 * @returns {() => void} unsubscribe function
 */
export function onThemeChange(cb) {
	listeners.push(cb);
	return () => {
		const idx = listeners.indexOf(cb);
		if (idx > -1) listeners.splice(idx, 1);
	};
}

/**
 * Apply a theme to the document root with transition class.
 * @param {'day' | 'night'} theme
 */
function applyTheme(theme) {
	const root = document.documentElement;

	// Add transition class for smooth theme switch
	root.classList.add("theme-transitioning");
	root.dataset.theme = theme;

	// Remove transition class after animation completes
	setTimeout(() => {
		root.classList.remove("theme-transitioning");
	}, 420);

	// Notify listeners
	for (const cb of listeners) {
		try {
			cb(theme);
		} catch (e) {
			/* swallow */
		}
	}

	// Update theme-color meta tag for PWA
	const metaTheme = document.querySelector('meta[name="theme-color"]');
	if (metaTheme) {
		const color = theme === "day" ? "#f5f5f7" : "#161826";
		metaTheme.setAttribute("content", color);
	}
}
